import { PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteNoteConfirmModal } from './components/DeleteNoteConfirmModal';
import NoteCard from './components/NoteCard';
import NoteFormModal from './components/NoteFormModal';

import type { Note } from '@/apps/Notes/stores/useNotesStore';

import { useNotesStore } from '@/apps/Notes/stores/useNotesStore';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import { useContainerWidth } from '@/common/hooks/useContainerWidth';
import { useWidgetLinksStore } from '@/common/stores/useWidgetLinksStore';
import { LINK_TYPES } from '@/common/utils/constants';
import { getGridStyle } from '@/common/utils/grid';

const Notes = ({ widgetId }: { widgetId: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(ref);
  const { t } = useTranslation();

  const { notes, fetchNotes, deleteNote, loading } = useNotesStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editNote, setEditNote] = useState<Pick<Note, 'id' | 'title' | 'content'> | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<Pick<Note, 'id' | 'title'> | null>(null);

  const links = useWidgetLinksStore((state) => state.links);
  const fetchLinks = useWidgetLinksStore((state) => state.fetchLinks);

  useEffect(() => {
    fetchNotes(widgetId);
    fetchLinks();
  }, [fetchNotes, fetchLinks, widgetId]);

  const linkedNoteIds = useMemo(() => {
    const ids = new Set<string>();
    for (const link of links) {
      if (link.target_type === LINK_TYPES.NOTE) {
        ids.add(link.target_id);
      }
    }
    return ids;
  }, [links]);

  const handleEditNote = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId);
    if (note) {
      setEditNote({ id: note.id, title: note.title, content: note.content });
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditNote(null);
  };

  const handleDeleteClick = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId);
    if (note) setNoteToDelete({ id: note.id, title: note.title });
  };

  const handleConfirmDelete = async () => {
    if (noteToDelete) {
      await deleteNote(noteToDelete.id);
      setNoteToDelete(null);
    }
  };

  return (
    <div ref={ref} className="h-full flex flex-col">
      <div className="flex justify-end items-center sticky top-0 z-10 py-2 px-1">
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
          onClick={() => setIsModalOpen(true)}
        >
          <PlusIcon className="h-4 w-4" />
          {t('notes.newNote')}
        </button>
      </div>

      <div className="flex-1 overflow-auto px-1 pb-2">
        {loading && <LoadingSpinner />}

        <div style={getGridStyle(containerWidth)}>
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              content={note.content}
              createdAt={note.created_at}
              id={note.id}
              isLinkedToEvent={linkedNoteIds.has(note.id)}
              title={note.title}
              onDelete={handleDeleteClick}
              onEdit={handleEditNote}
            />
          ))}
        </div>
      </div>

      <NoteFormModal
        editNote={editNote}
        isOpen={isModalOpen}
        widgetId={widgetId}
        onClose={handleModalClose}
      />

      <DeleteNoteConfirmModal
        isOpen={!!noteToDelete}
        noteTitle={noteToDelete?.title}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default Notes;
